/**
 * src/components/GeoLinkPendingTradesPanel.tsx
 *
 * UI for the GeoLink → Zenex Trade pending-actions pipeline.
 *
 * Flow per §5 of `docs/geolink-zenex-trade-pending-actions.md`:
 *   1. `useGeoLinkPendingTrades` polls /open + /close every ~15s and
 *      deduplicates on `(rule_id, matched_public_key)`.
 *   2. For `open_market`: the panel renders an editable form (market,
 *      side, collateral, notional, TP, SL). GeoLink's rule parameters
 *      pre-populate the form but the user can override before executing.
 *   3. For `close_position`: the panel first calls
 *      /api/v2/zenex/lookup-open-position to confirm the user actually
 *      has an open position on-chain. Close rows with no matching
 *      position are hidden (no reason to nag the user).
 *   4. On execute: prepare-open-market / prepare-close-position →
 *      passkey-sign / wallet-sign → fee-pay-and-submit → /complete.
 *   5. Cancel sends /api/contracts/rules/pending/trades/<id>/cancel with
 *      one of the canonical reason codes.
 *
 * The Pyth price update is ALWAYS fetched server-side (no UI control
 * for it) because the trading contract's verifier expects the exact
 * opaque bytes the Pyth service emitted.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  cancelTrade,
  cancelReasonLabel,
  completeTrade,
  tradeLogicalKey,
  type CancelReason,
  type PendingTradeAction,
  type TradeActionStatus,
} from '../services/geoLinkTradeService';
import {
  getZenexMarkets,
  prepareOpenMarket,
  prepareClosePosition,
  lookupOpenPositions,
  feePayAndSubmit,
  fetchZenexPrice,
  submitClassicSorobanXdr,
  ZENEX_MARKET_FEED_MAP,
  type ZenexMarketInfo,
  type ZenexOpenPosition,
  type ZenexStructuredError,
} from '../services/zenexTradeService';
import { useGeoLinkPendingTrades } from '../hooks/useGeoLinkPendingTrades';
import type { SmartAccountRecord } from '../services/passkeyKitService';

// ── Color tokens — mirror the deposits panel so the two feel paired ───────────
const C = {
  bg:           '#fff',
  bgCard:       '#f8f9fa',
  bgCardHover:  '#f0f0f0',
  border:       '#ddd',
  borderFaint:  '#eee',
  text:         '#333',
  textMuted:    '#555',
  textFaint:    '#888',
  gold:         '#FFD700',
  goldDark:     '#996600',
  goldDim:      'rgba(255,215,0,0.15)',
  green:        '#28a745',
  greenDim:     'rgba(40,167,69,0.1)',
  blue:         '#007bff',
  blueDim:      'rgba(0,123,255,0.1)',
  amber:        '#fd7e14',
  amberDim:     'rgba(253,126,20,0.1)',
  red:          '#dc3545',
  redDim:       'rgba(220,53,69,0.1)',
};

// Zenex trading palette — pulled from zenex.trade. We inject these only
// into Zenex-specific controls (side pills, leverage slider + presets,
// side badge, open_market execute button) so the trading affordances feel
// native to Zenex. The rest of the card keeps GeoTrust's gold/cream chrome
// so the pending-action list reads as one coherent surface.
const ZX = {
  long:       '#2EBD85', // emerald — Zenex "Long/Buy"
  longDim:    'rgba(46,189,133,0.12)',
  longDeep:   '#1C8A60', // darker emerald for active text / borders
  short:      '#F6465D', // signature trading red-pink
  shortDim:   'rgba(246,70,93,0.12)',
  shortDeep:  '#C8364A',
};

// Collateral / notional live on the USDC SAC which is 7 decimals.
// Trigger prices (TP/SL) use the market's `price_decimals` — all current
// testnet markets are 8, so we hard-code it here. If new markets land with
// other decimals, read `markets[marketId].price_decimals` instead.
const USDC_DECIMALS        = 7;
const DEFAULT_PRICE_DECIMS = 8;

// Zenex hosts the trading dashboard at https://zenex.trade/. We surface
// deep-links so users can cross-check positions and orders in the
// canonical UI whenever GeoTrust can't settle a pending action. Override
// via REACT_APP_ZENEX_APP_URL if Zenex ships a mainnet or staging host.
const ZENEX_APP_URL =
  process.env.REACT_APP_ZENEX_APP_URL?.replace(/\/$/, '') ||
  'https://zenex.trade';

export interface GeoLinkPendingTradesPanelProps {
  /** User's Stellar address (G- or C-address). Used as `matched_public_key`
   *  when polling GeoLink and as `user` on every Zenex call. */
  walletPublicKey: string | null;
  /**
   * When the user's wallet is a C-address, the caller MUST provide a
   * passkey-backed signer. The panel never touches WebAuthn directly —
   * the parent owns the `passkeyKitService` instance and picks the right
   * smart-account record.
   */
  signUnsignedXdr?: (
    unsignedXdr: string,
    latestLedger?: number,
  ) => Promise<string>;
  /** When the user's wallet is a G-address, pass a classic wallet signer.
   *  The panel skips the fee-pay-and-submit helper and returns the signed
   *  XDR to the parent to submit however it prefers. */
  signClassicXdr?: (
    unsignedXdr: string,
    networkPassphrase: string,
  ) => Promise<string>;
  /**
   * Optional — used only when `signClassicXdr` is not provided and the
   * caller still wants the panel to work for G-address flows (e.g. via
   * Horizon + service account). This closes the loop so the panel can
   * get a confirmed tx hash back.
   */
  submitClassicSignedXdr?: (signedXdr: string) => Promise<{ hash: string; ledger?: number }>;
  network?: 'testnet' | 'mainnet';
  pollIntervalMs?: number;
  onPendingCountChange?: (count: number) => void;
  onPositionOpened?: (info: { user: string; marketId: number; positionId: number; txHash: string }) => void;
  /** The active smart-account record (only used for the "signing with" badge). */
  activePasskey?: SmartAccountRecord | null;
  /**
   * One-click trustline establishment.  When provided and the panel has
   * surfaced a `missing_trustline` error with `asset` metadata, an
   * inline "+ Add this trustline" button appears in the error block so
   * the user doesn't need to leave the trades panel, hunt down the
   * Trustlines tab, and re-type the asset code + issuer that the
   * server already gave us.
   *
   * The parent owns the on-chain mechanics (load source account from
   * Horizon, build `Operation.changeTrust`, sign with the connected
   * wallet, submit via Horizon).  The panel just waits on the promise,
   * shows a spinner, and on success clears the error + triggers a poll
   * so the trade can be retried immediately.
   *
   * Skip wiring this for smart-account (C-address) wallets — they
   * don't hold classic Stellar trustlines and the changeTrust op would
   * be meaningless.
   */
  onAddTrustline?: (asset: { code: string; issuer: string }) => Promise<void>;
}

// ── Status config ─────────────────────────────────────────────────────────────
// Server vocabulary (post commit c55be5b): pending | completed | cancelled |
// not_pending | expired. On-chain reverts surface as `cancelled` with a
// non-null `reason` — there is no separate `failed` status server-side.
// The list endpoints only ever emit `pending`; the terminal values appear
// on POST /cancel responses and GET /:action_id.

const STATUS_CONFIG: Record<
  TradeActionStatus,
  { label: string; color: string; dim: string; border: string; icon: string }
> = {
  pending:     { label: 'Pending',     color: C.amber,     dim: C.amberDim, border: '#fd7e1444', icon: '⏳' },
  completed:   { label: 'Completed',   color: C.green,     dim: C.greenDim, border: '#28a74544', icon: '✓'  },
  cancelled:   { label: 'Cancelled',   color: C.red,       dim: C.redDim,   border: '#dc354544', icon: '✕'  },
  not_pending: { label: 'Not Pending', color: C.textFaint, dim: '#f5f5f5',  border: '#eeeeee',   icon: '—' },
  expired:     { label: 'Expired',     color: C.textFaint, dim: '#f5f5f5',  border: '#eeeeee',   icon: '⌛' },
};

const CANCEL_REASONS: { value: CancelReason; label: string }[] = [
  { value: 'user_declined',            label: 'User declined'           },
  { value: 'price_feed_unavailable',   label: 'Price feed unavailable'  },
  { value: 'insufficient_balance',     label: 'Insufficient balance'    },
  { value: 'expired',                  label: 'Expired'                 },
  { value: 'other',                    label: 'Other'                   },
];

const NETWORK_PASSPHRASE = (net: 'testnet' | 'mainnet') =>
  net === 'mainnet'
    ? 'Public Global Stellar Network ; September 2015'
    : 'Test SDF Network ; September 2015';

// ── Open-market form ──────────────────────────────────────────────────────────
// Display values are in human units (USDC for amounts, USD for prices).
// They're converted to raw i128 strings right before we call the backend.
//
// We store `leverage` rather than `notionalUsdc` directly because that's how
// traders think about positions (and how zenex.trade's native UI works).
// Notional is derived at render + submit time as `collateral × leverage`.
interface OpenForm {
  marketId:        number;
  isLong:          boolean;
  collateralUsdc:  string;
  /** Integer multiplier, clamped to [MIN_LEVERAGE, MAX_LEVERAGE]. */
  leverage:        number;
  takeProfitPrice: string; // empty = no trigger
  stopLossPrice:   string; // empty = no trigger
}

// Zenex's BTC/ETH perps advertise up to 100x on testnet; XLM is similar.
// Individual markets MAY have a lower cap encoded in their `margin` config
// (max_leverage = 1 / margin), which the contract enforces on simulation.
// We don't hard-cap below 100x in the UI so a larger cap on any future
// market is still reachable; if the market's cap is lower, the simulation
// will reject and the user sees the contract error.
const MIN_LEVERAGE = 2;
const MAX_LEVERAGE = 100;
const LEVERAGE_PRESETS = [2, 5, 10, 25, 50, 100] as const;

type OpenPositionsState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; positions: ZenexOpenPosition[] };

// ── Helpers ───────────────────────────────────────────────────────────────────

function toRawUsdc(displayValue: string): string {
  // "10.5" USDC → "105000000" (7 decimals). Empty → "0".
  if (!displayValue || displayValue.trim() === '') return '0';
  const n = Number(displayValue);
  if (!Number.isFinite(n) || n < 0) return '0';
  // Preserve up to USDC_DECIMALS of precision without bigint-shimming.
  const rounded = Math.round(n * 10 ** USDC_DECIMALS);
  return String(rounded);
}

function toRawPrice(displayValue: string, priceDecimals = DEFAULT_PRICE_DECIMS): string {
  if (!displayValue || displayValue.trim() === '') return '0';
  const n = Number(displayValue);
  if (!Number.isFinite(n) || n < 0) return '0';
  const rounded = Math.round(n * 10 ** priceDecimals);
  return String(rounded);
}

function fromRawUsdc(rawValue: string | number | boolean | undefined): string {
  if (rawValue == null || rawValue === '' || rawValue === false) return '';
  try {
    const n = Number(rawValue);
    if (!Number.isFinite(n) || n === 0) return '';
    return (n / 10 ** USDC_DECIMALS).toString();
  } catch { return ''; }
}

function fromRawPrice(rawValue: string | number | boolean | undefined, priceDecimals = DEFAULT_PRICE_DECIMS): string {
  if (rawValue == null || rawValue === '' || rawValue === false) return '';
  try {
    const n = Number(rawValue);
    if (!Number.isFinite(n) || n === 0) return '';
    return (n / 10 ** priceDecimals).toString();
  } catch { return ''; }
}

function clampLeverage(x: number): number {
  if (!Number.isFinite(x) || x < MIN_LEVERAGE) return MIN_LEVERAGE;
  if (x > MAX_LEVERAGE) return MAX_LEVERAGE;
  return Math.round(x);
}

/**
 * Derive the display-friendly notional (e.g. "1000" USDC) from the current
 * form state. Trimmed to avoid long trailing zeroes (e.g. "50" not
 * "50.0000000"). Returns '0' for invalid / zero inputs so downstream
 * validation still catches them.
 */
function deriveNotionalUsdc(form: Pick<OpenForm, 'collateralUsdc' | 'leverage'>): string {
  const c = Number(form.collateralUsdc);
  if (!Number.isFinite(c) || c <= 0) return '0';
  const n = c * form.leverage;
  if (!Number.isFinite(n) || n <= 0) return '0';
  // USDC has 7-decimal precision; trim trailing zeros for readability.
  return n.toFixed(USDC_DECIMALS).replace(/\.?0+$/, '') || '0';
}

function seedOpenForm(trade: PendingTradeAction, markets: Record<string, ZenexMarketInfo>): OpenForm {
  const p = trade.parameters || {};
  const rawMarketId = Number(p.market_id ?? trade.price_hint?.market_id ?? 0);
  // Fall back to the first configured market if the rule carries 0 / unknown.
  const fallbackMarketId = Object.keys(markets)[0] ? Number(Object.keys(markets)[0]) : 1;
  const marketId = markets[String(rawMarketId)] ? rawMarketId : fallbackMarketId;

  const collateralUsdc  = fromRawUsdc(p.collateral    as string | undefined) || '10';
  const seededNotional  = fromRawUsdc(p.notional_size as string | undefined) || '50';

  // Derive initial leverage from the rule-provided collateral/notional
  // pair. Rules authored before leverage existed in the UI (~all current
  // ones) will land on whatever ratio they encoded. Fallback is 5x —
  // a conservative middle of the Zenex preset strip (2x..100x).
  const c = Number(collateralUsdc);
  const n = Number(seededNotional);
  const derivedLeverage = c > 0 && n > 0 ? Math.round(n / c) : 5;

  return {
    marketId,
    isLong:          p.is_long === true || p.is_long === 'true',
    collateralUsdc,
    leverage:        clampLeverage(derivedLeverage),
    takeProfitPrice: fromRawPrice(p.take_profit as string | undefined),
    stopLossPrice:   fromRawPrice(p.stop_loss   as string | undefined),
  };
}

// ── Live mark price + PnL ─────────────────────────────────────────────────────
// We share one Pyth price poll across every visible card by lifting the
// fetch into the panel and keying by feed_id. Cards then read their
// market's mark price out of the resulting map. This keeps request
// volume to ~one fetch per market per `intervalMs` regardless of how
// many cards are mounted (a fan-out of 5 close cards on the BTC market
// still only polls feed 1 once per tick).

interface MarkPriceMap {
  [feedId: number]: { priceUsd: number; fetchedAt: number };
}

function useMarketPrices(feedIds: number[], intervalMs: number = 10_000): MarkPriceMap {
  const [prices, setPrices] = useState<MarkPriceMap>({});
  // Stable cache key over the unique sorted set of feed ids — re-derived
  // only when the underlying set actually changes, NOT every render.
  const idsKey = useMemo(
    () => Array.from(new Set(feedIds.filter(n => Number.isFinite(n) && n > 0)))
            .sort((a, b) => a - b)
            .join(','),
    [feedIds],
  );

  useEffect(() => {
    if (!idsKey) return undefined;
    const ids = idsKey.split(',').map(Number);
    let cancelled = false;
    const refresh = async () => {
      try {
        const results = await Promise.allSettled(ids.map(id => fetchZenexPrice(id)));
        if (cancelled) return;
        const next: MarkPriceMap = {};
        results.forEach((r, i) => {
          if (r.status === 'fulfilled' && r.value.success && r.value.meta?.price != null) {
            const p   = Number(r.value.meta.price);
            const exp = Number(r.value.meta.exponent ?? 0);
            if (Number.isFinite(p) && Number.isFinite(exp)) {
              // Pyth convention: actual_usd = price × 10^exponent (exp is negative).
              next[ids[i]] = { priceUsd: p * Math.pow(10, exp), fetchedAt: Date.now() };
            }
          }
        });
        if (Object.keys(next).length > 0) {
          setPrices(prev => ({ ...prev, ...next }));
        }
      } catch {
        // best-effort — UI just shows '—' until the next tick
      }
    };
    refresh();
    const t = setInterval(refresh, intervalMs);
    return () => { cancelled = true; clearInterval(t); };
  }, [idsKey, intervalMs]);

  return prices;
}

interface PositionPnL {
  entryUsd:              number;
  markUsd:               number;
  /** Directional (already accounts for long vs short). */
  priceChangePct:        number;
  notionalUsdc:          number;
  collateralUsdc:        number;
  pnlUsdc:               number;
  netValueUsdc:          number;
  /** Trader's-eye view: `pnl / collateral × 100`. Hits ±100% at liquidation. */
  returnPctOnCollateral: number;
}

/**
 * NOTE: this is **mark-to-market price PnL only**. The on-chain
 * `position.collateral` value reflects whatever the contract currently
 * reports for the position (entry-time collateral on Zenex's perp model)
 * and we don't have access to the running funding/borrow accruals that
 * Zenex's UI nets out before showing "Net Value". So the number we
 * compute here will track Zenex's "Net Value" only on freshly-opened
 * positions and will diverge as funding fees pile up. The UI labels
 * this as "Mark PnL" and points the user to Zenex for the fee-net
 * total.
 *
 *   pnl_usdc        = notional × (mark / entry − 1) × side
 *   net_value_usdc  = collateral + pnl_usdc            (mark-to-market)
 *   return_pct      = pnl_usdc / collateral × 100      (trader's view;
 *                                                        ±100% at liq)
 */
function computePnL(
  position: ZenexOpenPosition,
  markPriceUsd: number | undefined,
  priceDecimals: number = DEFAULT_PRICE_DECIMS,
): PositionPnL | null {
  if (markPriceUsd == null || !Number.isFinite(markPriceUsd) || markPriceUsd <= 0) return null;
  if (!position.entry_price || !position.collateral || position.is_long == null) return null;

  const entryUsd       = Number(position.entry_price)   / 10 ** priceDecimals;
  const collateralUsdc = Number(position.collateral)    / 10 ** USDC_DECIMALS;
  const notionalUsdc   = Number(position.notional_size) / 10 ** USDC_DECIMALS;
  if (!Number.isFinite(entryUsd)       || entryUsd       <= 0) return null;
  if (!Number.isFinite(collateralUsdc) || collateralUsdc <= 0) return null;
  if (!Number.isFinite(notionalUsdc)   || notionalUsdc   <= 0) return null;

  const ratio = markPriceUsd / entryUsd;
  const pnlUsdc        = position.is_long ? notionalUsdc * (ratio - 1) : notionalUsdc * (1 - ratio);
  const priceChangePct = (position.is_long ? (ratio - 1) : (1 - ratio)) * 100;
  const netValueUsdc   = collateralUsdc + pnlUsdc;
  const returnPctOnCollateral = (pnlUsdc / collateralUsdc) * 100;

  return {
    entryUsd, markUsd: markPriceUsd,
    priceChangePct,
    notionalUsdc, collateralUsdc,
    pnlUsdc, netValueUsdc,
    returnPctOnCollateral,
  };
}

/** Format a USD price with the right precision for the asset's price band. */
function formatUsdPrice(usd: number | undefined): string {
  if (usd == null || !Number.isFinite(usd)) return '—';
  if (usd >= 1000) return `$${usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (usd >= 1)    return `$${usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;
  return `$${usd.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 6 })}`;
}

function formatUsdcDelta(usdc: number): string {
  if (!Number.isFinite(usdc)) return '—';
  const sign = usdc >= 0 ? '+' : '−';
  return `${sign}${Math.abs(usdc).toFixed(4)} USDC`;
}

function formatPctDelta(pct: number): string {
  if (!Number.isFinite(pct)) return '—';
  const sign = pct >= 0 ? '+' : '−';
  return `${sign}${Math.abs(pct).toFixed(2)}%`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export const GeoLinkPendingTradesPanel: React.FC<GeoLinkPendingTradesPanelProps> = ({
  walletPublicKey,
  signUnsignedXdr,
  signClassicXdr,
  submitClassicSignedXdr,
  network = 'testnet',
  pollIntervalMs = 15_000,
  onPendingCountChange,
  onPositionOpened,
  activePasskey,
  onAddTrustline,
}) => {
  const {
    opens, closes, loading, error: pollError, refresh, dismissByLogicalKey,
    lastFetchedAt,
  } = useGeoLinkPendingTrades({
    publicKey: walletPublicKey,
    pollIntervalMs,
    enabled: !!walletPublicKey,
  });

  const [executing, setExecuting] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  // One-click trustline establishment state.  `addingTrustline` is
  // truthy while the parent's onAddTrustline promise is pending so the
  // button can show a spinner and disable.  `trustlineAdded` flips to
  // the asset key after a successful submission so we can render a
  // green "✓ trustline added" badge for ~3s before the next poll
  // (which clears the missing_trustline error naturally).
  const [addingTrustline, setAddingTrustline] = useState<string | null>(null);
  const [trustlineAdded, setTrustlineAdded] = useState<string | null>(null);
  // Rich error state so we can render the server's translated hint alongside
  // the raw message. `raw` is only shown in a collapsed <details> block.
  const [err, setErr] = useState<{
    message:            string;
    hint?:              string;
    code?:              string;
    asset?:             { code: string; issuer: string };
    contractErrorCode?: number;
    contractErrorName?: string;
    raw?:               string;
  } | null>(null);
  const [lastHash, setLastHash] = useState<{ id: string; hash: string } | null>(null);

  /** Normalises any thrown error into our structured shape. */
  const captureError = useCallback((e: unknown, fallback: string) => {
    const se = e as ZenexStructuredError | undefined;
    setErr({
      message:           se?.message || fallback,
      hint:              se?.hint,
      code:              se?.code,
      asset:             se?.asset,
      contractErrorCode: se?.contractErrorCode,
      contractErrorName: se?.contractErrorName,
      raw:               se?.rawError,
    });
  }, []);

  /**
   * One-click handler for the `+ Add this trustline` button.  Delegates
   * the on-chain mechanics to the parent (see `onAddTrustline` prop
   * docs); we just track busy state, clear the error on success, and
   * trigger a poll so the trade can be retried immediately.
   *
   * Failures bubble up through `captureError` so the same error UI
   * surface used for trade simulation errors covers trustline
   * submission failures (insufficient base reserve, sequence mismatch,
   * user rejected the signature, etc.) without a parallel branch.
   */
  const handleAddTrustline = useCallback(async (asset: { code: string; issuer: string }) => {
    if (!onAddTrustline) return;
    const key = `${asset.code}:${asset.issuer}`;
    setAddingTrustline(key);
    try {
      await onAddTrustline(asset);
      setTrustlineAdded(key);
      setErr(null);
      window.setTimeout(() => {
        setTrustlineAdded(prev => (prev === key ? null : prev));
      }, 3000);
      refresh().catch(() => { /* poll error already surfaces via pollError */ });
    } catch (e: unknown) {
      captureError(e, 'Failed to add trustline');
    } finally {
      setAddingTrustline(null);
    }
  }, [onAddTrustline, refresh, captureError]);

  // Live market list from the backend. Seeded with the shared offline map
  // (`ZENEX_MARKET_FEED_MAP`) so rule labels never flash "market 0" while the
  // /api/v2/zenex/markets request is in flight. The server response replaces
  // this once it arrives, so any newly-configured markets show up automatically.
  const [markets, setMarkets] = useState<Record<string, ZenexMarketInfo>>(
    () => ({ ...ZENEX_MARKET_FEED_MAP }),
  );

  // Per-trade editable form state (open_market only).
  const [openForms, setOpenForms] = useState<Record<string, OpenForm>>({});

  // On-chain open positions for the user. Used to (a) gate close-position
  // rendering so we don't show actions the contract would reject, and
  // (b) fan a single GeoLink close_position pending action out into one
  // actionable card per open position.
  const [openPositions, setOpenPositions] = useState<OpenPositionsState>({ status: 'idle' });

  // Since commit c55be5b, GeoLink bypasses rule-level rate-limit AND
  // cancel-suppression on `close_position`, so a new pending re-emits on
  // the very next location tick after either /complete or /cancel. That
  // removes the cross-session concern we originally had here.
  //
  // Within a SINGLE fan-out burst, though — user closes N positions in
  // seconds before the next ~15s poll — the client still holds N cards
  // sharing ONE `trade.id`. GeoLink's /complete is idempotent-per-id but
  // the server payload only carries one (position_id, transaction_hash)
  // pair, so repeating /complete on the same id with different payloads
  // isn't meaningful. This ref ensures we call /complete once per
  // `trade.id`; subsequent closes in the burst settle on-chain only and
  // the next poll picks up the fresh pending GeoLink re-emits.
  const completedTradeIdsRef = useRef<Set<string>>(new Set());

  // ── Load /markets once ──────────────────────────────────────────────────────
  // Overlays the server's authoritative map on top of the offline fallback so
  // newly-configured markets show up immediately while known markets keep
  // their labels even if the request fails.
  useEffect(() => {
    let cancelled = false;
    getZenexMarkets()
      .then(r => {
        if (cancelled) return;
        const live = r.markets ?? {};
        setMarkets(prev => ({ ...prev, ...live }));
      })
      .catch(() => { /* non-fatal — fallback map already seeded */ });
    return () => { cancelled = true; };
  }, []);

  // ── Seed the open-market form whenever a new open trade appears ────────────
  useEffect(() => {
    setOpenForms(prev => {
      const next = { ...prev };
      let changed = false;
      for (const trade of opens) {
        if (!next[trade.id]) {
          next[trade.id] = seedOpenForm(trade, markets);
          changed = true;
        }
      }
      // Drop form state for trades that are gone (complete/cancel/new poll).
      const liveIds = new Set(opens.map(t => t.id));
      for (const id of Object.keys(next)) {
        if (!liveIds.has(id)) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [opens, markets]);

  // ── Lookup user's open positions when close trades appear ──────────────────
  // We scan ALL filled non-zero positions (not just the newest) so the
  // panel can fan a single GeoLink close_position action out into one
  // actionable card per position. Without this fan-out the user would be
  // stuck with one close card and N open positions, forced to guess which
  // one GeoTrust is about to close.
  //
  // We also re-run this lookup on every pending-trades poll tick (keyed
  // on `lastFetchedAt`) so we catch out-of-band closes. Example: user
  // closes a position directly on zenex.trade → GeoLink still has no
  // idea, but within ~15s we'll drop that position from the fan-out and
  // the stale card disappears on its own.
  //
  // Cost: one Soroban simulation per poll while a close is pending. The
  // simulation iterates the user's position counter server-side so it's
  // O(counter) per call, which for typical users is <10 positions.
  //
  // The `status === 'ready' ? prev : loading` guard keeps the UI from
  // flashing a loading state between ticks when we already have data.
  useEffect(() => {
    if (!walletPublicKey) { setOpenPositions({ status: 'idle' }); return; }
    if (closes.length === 0) { setOpenPositions({ status: 'idle' }); return; }

    let cancelled = false;
    setOpenPositions(prev => (prev.status === 'ready' ? prev : { status: 'loading' }));
    lookupOpenPositions({ user: walletPublicKey })
      .then(r => {
        if (cancelled) return;
        const positions = r.success && r.positions ? r.positions : [];
        setOpenPositions({ status: 'ready', positions });
      })
      .catch(() => {
        if (!cancelled) setOpenPositions({ status: 'ready', positions: [] });
      });
    return () => { cancelled = true; };
  }, [walletPublicKey, closes.length, lastHash, lastFetchedAt]);

  // ── Sign + submit helper ────────────────────────────────────────────────────
  const signAndSubmit = useCallback(async (
    unsignedXdr: string,
    latestLedger: number,
    authMode: 'smart-account' | 'classic',
  ): Promise<{ hash: string; ledger?: number }> => {
    if (authMode === 'smart-account') {
      if (!signUnsignedXdr) {
        throw new Error('Passkey signer is not available. Pair your smart account before executing.');
      }
      const signed = await signUnsignedXdr(unsignedXdr, latestLedger);
      const submit = await feePayAndSubmit(signed);
      if (!submit.hash) throw new Error('Submit did not return a tx hash');
      return { hash: submit.hash, ledger: submit.ledger };
    }
    if (!signClassicXdr) {
      throw new Error('No wallet signer available. Connect a wallet to sign the transaction.');
    }
    const signed = await signClassicXdr(unsignedXdr, NETWORK_PASSPHRASE(network));
    // Parent-supplied submit callback wins (useful for testing / alternate
    // wallets), otherwise fall back to Soroban RPC's sendTransaction +
    // getTransaction polling — Zenex ops are Soroban invocations so they
    // MUST be submitted through Soroban RPC, not Horizon.
    if (submitClassicSignedXdr) return submitClassicSignedXdr(signed);
    return submitClassicSorobanXdr(signed, {
      networkPassphrase: NETWORK_PASSPHRASE(network),
    });
  }, [signUnsignedXdr, signClassicXdr, submitClassicSignedXdr, network]);

  // ── Execute: open_market ────────────────────────────────────────────────────
  const executeOpen = useCallback(async (trade: PendingTradeAction) => {
    if (!walletPublicKey) return;
    setErr(null);
    setExecuting(trade.id);
    try {
      const form = openForms[trade.id] ?? seedOpenForm(trade, markets);
      if (!form.marketId) throw new Error('Pick a market before executing');

      const collateralRaw = toRawUsdc(form.collateralUsdc);
      // Notional is derived at submit time as `collateral × leverage`;
      // this keeps the source-of-truth in the form state simple (user
      // only picks collateral + leverage) and matches Zenex's own UI.
      const notionalRaw   = toRawUsdc(deriveNotionalUsdc(form));
      if (collateralRaw === '0') throw new Error('Collateral must be > 0');
      if (notionalRaw   === '0') throw new Error('Notional size must be > 0 (check collateral × leverage)');

      const prep = await prepareOpenMarket({
        user:          walletPublicKey,
        market_id:     form.marketId,
        collateral:    collateralRaw,
        notional_size: notionalRaw,
        is_long:       form.isLong,
        take_profit:   toRawPrice(form.takeProfitPrice),
        stop_loss:     toRawPrice(form.stopLossPrice),
      });
      if (!prep.success || !prep.unsignedXdr) {
        throw new Error(prep.error || 'Failed to prepare open_market tx');
      }

      const { hash, ledger } = await signAndSubmit(prep.unsignedXdr, prep.latestLedger, prep.authMode);

      const resp = await completeTrade(trade.id, {
        public_key:       walletPublicKey,
        transaction_hash: hash,
        ledger:           ledger ?? null,
        market_id:        form.marketId,
        position_id:      null,
      });
      if (!resp.success) throw new Error(resp.error || 'completeTrade failed');

      onPositionOpened?.({ user: walletPublicKey, marketId: form.marketId, positionId: -1, txHash: hash });
      dismissByLogicalKey(tradeLogicalKey(trade));
      setLastHash({ id: trade.id, hash });
      await refresh();
    } catch (e) {
      captureError(e, 'Execute open_market failed');
    } finally {
      setExecuting(null);
    }
  }, [walletPublicKey, openForms, markets, signAndSubmit, refresh, dismissByLogicalKey, onPositionOpened, captureError]);

  // ── Execute: close_position ─────────────────────────────────────────────────
  // Called with the specific position we want to close — the caller (the
  // render-time fan-out) binds a `ZenexOpenPosition` per card so the user
  // can close any of their open positions while in the close geofence.
  //
  // After commit c55be5b, GeoLink re-emits a fresh pending on the next
  // location tick after /complete — no rule-level suppression. We still
  // only call /complete once per `trade.id` in a fan-out burst though,
  // because the payload carries a single (position_id, tx_hash) pair and
  // re-submitting the same id with different payloads isn't well-defined.
  // Subsequent closes in the burst settle on-chain only; the fresh pending
  // emits on the next poll and picks up any remaining positions.
  const executeClose = useCallback(async (
    trade: PendingTradeAction,
    position: ZenexOpenPosition,
  ) => {
    if (!walletPublicKey) return;
    setErr(null);
    const cardId = `${trade.id}:${position.id}`;
    setExecuting(cardId);
    try {
      const prep = await prepareClosePosition({
        user:        walletPublicKey,
        position_id: position.id,
        market_id:   position.market_id,
      });
      if (!prep.success || !prep.unsignedXdr) {
        throw new Error(prep.error || 'Failed to prepare close_position tx');
      }

      const { hash, ledger } = await signAndSubmit(prep.unsignedXdr, prep.latestLedger, prep.authMode);

      // Only /complete once per GeoLink action id. Subsequent closes in
      // the fan-out burst skip the server call — /complete is idempotent-
      // per-id but the payload is single-position, so re-submitting with
      // a different position_id isn't meaningful on GeoLink's side.
      const alreadyCompleted = completedTradeIdsRef.current.has(trade.id);
      if (!alreadyCompleted) {
        try {
          const resp = await completeTrade(trade.id, {
            public_key:       walletPublicKey,
            transaction_hash: hash,
            ledger:           ledger ?? null,
            market_id:        position.market_id,
            position_id:      position.id,
          });
          if (!resp.success) throw new Error(resp.error || 'completeTrade failed');
          completedTradeIdsRef.current.add(trade.id);
          dismissByLogicalKey(tradeLogicalKey(trade));
        } catch (completeErr) {
          // /complete failed AFTER the on-chain close succeeded. The
          // position IS closed; only GeoLink's bookkeeping is off. Surface
          // the problem but don't treat the whole action as failed.
          completedTradeIdsRef.current.add(trade.id);
          dismissByLogicalKey(tradeLogicalKey(trade));
        }
      }
      setLastHash({ id: cardId, hash });
      // Drop the closed position from state immediately so the card
      // disappears without waiting for the next 15s poll.
      setOpenPositions(prev => {
        if (prev.status !== 'ready') return prev;
        return { status: 'ready', positions: prev.positions.filter(p => p.id !== position.id) };
      });
      await refresh();
    } catch (e) {
      // When the contract says the position doesn't exist (720) we KNOW
      // the cached lookup is stale — either the position was already
      // closed or our filled-heuristic picked up a phantom entry. Drop
      // the stale entry from local state and re-fetch so the remaining
      // cards stay accurate.
      const se = e as ZenexStructuredError | undefined;
      if (se?.code === 'position_not_found' && walletPublicKey) {
        setOpenPositions(prev => {
          if (prev.status !== 'ready') return prev;
          return { status: 'ready', positions: prev.positions.filter(p => p.id !== position.id) };
        });
        // Kick a fresh scan — if the user has no remaining positions,
        // `visibleCloseCards` collapses to [] and the hidden-closes hint
        // surfaces to explain why.
        try {
          const look = await lookupOpenPositions({ user: walletPublicKey });
          setOpenPositions({
            status: 'ready',
            positions: look.success ? look.positions : [],
          });
        } catch { /* best-effort */ }
      }
      captureError(e, 'Execute close_position failed');
    } finally {
      setExecuting(null);
    }
  }, [walletPublicKey, signAndSubmit, refresh, dismissByLogicalKey, captureError]);

  // ── Cancel ──────────────────────────────────────────────────────────────────
  // POST /cancel contract (commit c55be5b, integration-guide §4.2):
  //
  //   200 fresh success         → body.success=true, no already_terminal
  //   200 idempotent terminal   → body.already_terminal=true,
  //                               body.status ∈ {cancelled, completed, not_pending}
  //                               body.trade_action.reason echoes the ORIGINAL
  //                               cancel's reason (not the one we just sent).
  //   404 not found             → thrown by request(), status=404
  //   500 internal              → thrown by request(), status=500 — REAL fault
  //
  // The old "500 / 503 → assume reconciled upstream" fallback is gone: per
  // the new contract, 5xx is always a genuine server fault and the user
  // should see it, not a silent dismissal.
  const handleCancel = useCallback(async (trade: PendingTradeAction, reason: CancelReason) => {
    if (!walletPublicKey) return;
    setErr(null);
    setCancelling(trade.id);
    try {
      const resp = await cancelTrade(trade.id, { public_key: walletPublicKey, reason });

      if (resp.already_terminal) {
        // Action was resolved out-of-band (another session, or a prior
        // cancel). Dismiss locally and show what the server says it is.
        dismissByLogicalKey(tradeLogicalKey(trade));
        const storedReason = cancelReasonLabel(resp.trade_action?.reason);
        const status = resp.status ?? resp.trade_action?.status ?? 'cancelled';
        const suffix = storedReason ? ` — ${storedReason}` : '';
        setErr({
          message: `Already ${status}${suffix}`,
          hint:
            status === 'completed'
              ? 'This action was already completed in a prior session. Dismissed locally.'
            : status === 'not_pending'
              ? 'This action was never actually pending on GeoLink; nothing to cancel.'
              : 'This action had already been cancelled. Dismissed locally.',
          code: 'already_terminal',
        });
      } else {
        // Fresh cancel (pending → cancelled). trade_action.cancelled_at is
        // set; we don't surface it in the UI today — the next poll will
        // drop the row anyway.
        dismissByLogicalKey(tradeLogicalKey(trade));
      }
      await refresh();
    } catch (e) {
      // `request()` decorates thrown errors with a numeric `status`.
      const httpErr = e as (Error & { status?: number }) | undefined;
      const status  = httpErr?.status;
      if (status === 404) {
        // (action_id, public_key) pair doesn't exist. Likely post-TTL
        // cleanup or an id-churn race. Dismiss locally.
        dismissByLogicalKey(tradeLogicalKey(trade));
        setErr({
          message: 'This pending action is no longer available on GeoLink.',
          hint:    'It may have expired past its 24h TTL or been resolved in another session. Dismissed locally.',
          code:    'not_found',
        });
        await refresh();
      } else {
        // 400 / 5xx / network. Real fault — surface it.
        captureError(e, 'Cancel failed');
      }
    } finally {
      setCancelling(null);
    }
  }, [walletPublicKey, refresh, dismissByLogicalKey, captureError]);

  // ── Visible trades ──────────────────────────────────────────────────────────
  // Opens: always visible (user can always open a new position).
  // Closes: fanned out by (pending close action) × (user's open positions).
  //         If the rule pinned a `market_id`, only positions in that
  //         market qualify; otherwise every open position gets its own
  //         close card. This lets the user pick which of their N
  //         positions to close while in the close geofence.
  const visibleCloseCards = useMemo(() => {
    if (openPositions.status !== 'ready') return [];
    const positions = openPositions.positions;
    if (positions.length === 0) return [];
    const pendingCloses = closes.filter(t => t.status === 'pending');
    const cards: { trade: PendingTradeAction; position: ZenexOpenPosition; cardId: string }[] = [];
    for (const trade of pendingCloses) {
      const ruleMarketId = Number(trade.parameters?.market_id ?? 0);
      const candidates = ruleMarketId > 0
        ? positions.filter(p => p.market_id === ruleMarketId)
        : positions;
      for (const position of candidates) {
        cards.push({ trade, position, cardId: `${trade.id}:${position.id}` });
      }
    }
    return cards;
  }, [closes, openPositions]);

  const visibleOpens = useMemo(
    () => opens.filter(t => t.status === 'pending'),
    [opens],
  );

  const visibleActionable = useMemo(
    () => visibleOpens.length + visibleCloseCards.length,
    [visibleOpens, visibleCloseCards],
  );

  // Live mark prices for every market we currently render — fanned out
  // by the same set of feed_ids that the visible cards reference. The
  // hook dedups by feed_id so 5 BTC close cards still cost one fetch
  // per tick. Refreshes every ~10s; cards display '—' until first tick.
  const visibleFeedIds = useMemo(() => {
    const ids = new Set<number>();
    for (const t of visibleOpens) {
      const mid = Number(t.parameters?.market_id ?? t.price_hint?.market_id ?? 0);
      const m = markets[String(mid)];
      if (m?.feed_id) ids.add(m.feed_id);
    }
    for (const c of visibleCloseCards) {
      const m = markets[String(c.position.market_id)];
      if (m?.feed_id) ids.add(m.feed_id);
    }
    return Array.from(ids);
  }, [visibleOpens, visibleCloseCards, markets]);
  const markPrices = useMarketPrices(visibleFeedIds, 10_000);

  // Propagate visible pending count (used by parent badges).
  useEffect(() => {
    onPendingCountChange?.(visibleActionable);
  }, [visibleActionable, onPendingCountChange]);

  const isSmartAccount = !!walletPublicKey && walletPublicKey.startsWith('C');

  const updateOpenForm = useCallback((tradeId: string, patch: Partial<OpenForm>) => {
    setOpenForms(prev => ({
      ...prev,
      [tradeId]: { ...(prev[tradeId] as OpenForm), ...patch },
    }));
  }, []);

  // How many pending close-rule actions were hidden because the user has
  // no matching open position? When positions are still loading we treat
  // the count as 0 so we don't flash a misleading "hidden" hint before
  // the lookup finishes. MUST be declared before any conditional early
  // return to satisfy the rules-of-hooks.
  const hiddenCloseCount = useMemo(() => {
    if (openPositions.status !== 'ready') return 0;
    const positions = openPositions.positions;
    const pendingCloses = closes.filter(t => t.status === 'pending');
    let hidden = 0;
    for (const trade of pendingCloses) {
      const ruleMarketId = Number(trade.parameters?.market_id ?? 0);
      const matchCount = ruleMarketId > 0
        ? positions.filter(p => p.market_id === ruleMarketId).length
        : positions.length;
      if (matchCount === 0) hidden += 1;
    }
    return hidden;
  }, [closes, openPositions]);

  if (!walletPublicKey) {
    return (
      <div style={{ padding: 32, textAlign: 'center', color: C.textFaint, fontSize: 13 }}>
        Connect a wallet to view GeoLink-qualified Zenex trades.
      </div>
    );
  }

  return (
    <div style={{ background: C.bg, minHeight: '100%', padding: 0, color: C.text }}>
      <style>{`
        .gzt-card { transition: background .15s; }
        .gzt-card:hover { background: ${C.bgCardHover} !important; }
        .gzt-exec  { background: ${C.gold}; color: #000; border: none; border-radius: 6px; font-size: 12px; font-weight: 700; cursor: pointer; transition: opacity .15s, background .15s, transform .15s, box-shadow .15s; }
        .gzt-exec:hover:not(:disabled) { opacity: .9; transform: translateY(-1px); }
        .gzt-exec:disabled { opacity: .4; cursor: not-allowed; transform: none; }
        /* Open_market gets side-tinted Zenex fills (green/red) with a soft
           matching glow. Close_position keeps the plain GeoTrust gold
           (the pulse lives on the CLOSE POSITION kind-chip instead, so
           the "act soon" signal sits at the top of the card where the
           eye already lands). */
        .gzt-exec.zx-long  { background: ${ZX.long};  color: #fff; box-shadow: 0 0 0 1px ${ZX.longDeep}, 0 2px 8px rgba(46,189,133,.35); }
        .gzt-exec.zx-short { background: ${ZX.short}; color: #fff; box-shadow: 0 0 0 1px ${ZX.shortDeep}, 0 2px 8px rgba(246,70,93,.35); }

        /* ── Unified chip system for the card header ────────────────────────
           One clean "modern" pill treatment shared by the status, kind,
           and network chips: white surface, neutral 1px grey border,
           small uppercase bold text. The only per-chip variation is the
           text color (and the small leading icon, for status). That
           eliminates the earlier visual noise on a minimized card —
           no more dark "black button" fills, no alpha-tinted amber
           borders blending to beige on white. */
        .gzt-chip {
          display: inline-flex; align-items: center; gap: 5px;
          background: #fff; color: ${C.textMuted};
          border: 1px solid ${C.border};
          border-radius: 999px;
          padding: 2px 10px;
          font-size: 10px; font-weight: 700; letter-spacing: .4px;
          text-transform: uppercase;
          white-space: nowrap;
        }
        .gzt-chip.status-pending   { color: ${C.amber}; }
        .gzt-chip.status-completed { color: ${C.green}; }
        .gzt-chip.status-cancelled { color: ${C.red};   }
        .gzt-chip.status-neutral   { color: ${C.textFaint}; }
        .gzt-chip.kind-open        { color: ${ZX.longDeep};  }
        .gzt-chip.kind-close       { color: ${ZX.shortDeep}; }
        .gzt-chip.network          { color: ${C.textFaint};  }
        .gzt-cancel { background: transparent; color: ${C.red}; border: 1px solid ${C.red}55; border-radius: 6px; font-size: 12px; font-weight: 600; cursor: pointer; transition: background .15s; }
        .gzt-cancel:hover:not(:disabled) { background: ${C.redDim}; }
        .gzt-cancel:disabled { opacity: .4; cursor: not-allowed; }
        .gzt-refresh-btn { background: transparent; border: 1px solid ${C.border}; color: ${C.goldDark}; border-radius: 6px; width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 14px; transition: background .15s; }
        .gzt-refresh-btn:hover:not(:disabled) { background: ${C.goldDim}; }
        .gzt-refresh-btn:disabled { opacity: .4; cursor: not-allowed; }
        .gzt-input { width: 100%; padding: 7px 9px; font-family: monospace; font-size: 12px; border: 1px solid ${C.border}; border-radius: 6px; background: #fff; color: ${C.text}; outline: none; box-sizing: border-box; }
        .gzt-input:focus { border-color: ${C.gold}; }
        .gzt-label { font-size: 9px; color: ${C.textFaint}; text-transform: uppercase; letter-spacing: .4px; margin-bottom: 4px; font-weight: 600; }
        .gzt-seg   { display: flex; border: 1px solid ${C.border}; border-radius: 6px; overflow: hidden; width: fit-content; }
        .gzt-seg button { padding: 6px 14px; font-size: 11px; font-weight: 600; border: none; background: #fff; color: ${C.textFaint}; cursor: pointer; transition: background .15s, color .15s, box-shadow .15s; }
        .gzt-seg button.active { font-weight: 700; }
        /* Zenex-style active tab: tinted background + darker text + accent
           underline. Matches the Long/Short selector on zenex.trade. */
        .gzt-seg button.long.active  { background: ${ZX.longDim};  color: ${ZX.longDeep};  box-shadow: inset 0 -2px 0 ${ZX.long}; }
        .gzt-seg button.short.active { background: ${ZX.shortDim}; color: ${ZX.shortDeep}; box-shadow: inset 0 -2px 0 ${ZX.short}; }
      `}</style>

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '14px 16px 10px',
        borderBottom: `1px solid ${C.borderFaint}`,
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700 }}>GeoLink-Qualified Zenex Trades</span>
            {visibleActionable > 0 && (
              <span style={{
                background: C.amberDim, color: C.amber, border: `1px solid ${C.amber}44`,
                borderRadius: 10, padding: '1px 7px', fontSize: 10, fontWeight: 700,
              }}>
                {visibleActionable} pending
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: C.textFaint, marginTop: 2 }}>
            {loading ? '⟳ Polling GeoLink…' : 'Qualified by GeoLink · Awaiting GeoTrust settlement'}
            {isSmartAccount && activePasskey && (
              <span style={{ marginLeft: 8, color: C.goldDark }}>
                · Signing with passkey <code style={{ background: '#f5f5f5', padding: '0 4px', borderRadius: 4 }}>
                  {activePasskey.accountAddress.slice(0, 6)}…{activePasskey.accountAddress.slice(-4)}
                </code>
              </span>
            )}
          </div>
        </div>
        <a
          href={ZENEX_APP_URL}
          target="_blank"
          rel="noopener noreferrer"
          title="Open Zenex dashboard — view and manage positions directly"
          style={{
            padding: '5px 9px', borderRadius: 6,
            border: `1px solid ${C.border}`, color: C.goldDark,
            fontSize: 11, fontWeight: 600, textDecoration: 'none',
            whiteSpace: 'nowrap', background: 'transparent',
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}
        >
          Zenex <span style={{ fontSize: 9 }}>↗</span>
        </a>
        <button className="gzt-refresh-btn" onClick={() => refresh()} disabled={loading} title="Refresh">
          {loading ? '…' : '↻'}
        </button>
      </div>

      {/* ── Errors ──────────────────────────────────────────────────────────── */}
      {/* Structured trade errors carry a `hint` + optional asset info. The raw
          simulation dump is hidden behind a <details> expander so it doesn't
          overwhelm the panel but is still available for debugging. */}
      {(err || pollError) && (
        <div style={{
          margin: '10px 14px', padding: '10px 12px', borderRadius: 7,
          background: C.redDim, border: `1px solid ${C.red}44`,
          color: C.red, fontSize: 12,
        }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
            <span>⚠</span>
            <strong style={{ flex: 1 }}>{err?.message || pollError}</strong>
            {err?.contractErrorCode != null && (
              <span style={{
                padding: '1px 6px', borderRadius: 3,
                background: '#fff', border: `1px solid ${C.red}44`,
                color: C.red, fontSize: 10, fontFamily: 'monospace',
                whiteSpace: 'nowrap',
              }}
                title={`Zenex contract error #${err.contractErrorCode}`}>
                {err.contractErrorName || `#${err.contractErrorCode}`}
              </span>
            )}
          </div>
          {err?.hint && (
            <div style={{ marginTop: 6, color: C.textMuted, fontSize: 12, lineHeight: 1.45 }}>
              {err.hint}
            </div>
          )}
          {err?.asset && (() => {
            // One-click trustline establishment.  Only surface the
            // button when the parent supplied `onAddTrustline` AND the
            // structured error is specifically a missing trustline
            // (other errors that happen to carry asset metadata, e.g.
            // insufficient_collateral, shouldn't trigger a changeTrust
            // op — those need a deposit, not a trustline).
            const assetKey = `${err.asset.code}:${err.asset.issuer}`;
            const isMissingTrustline = err.code === 'missing_trustline';
            const canAdd = !!onAddTrustline && isMissingTrustline;
            const isAdding = addingTrustline === assetKey;
            const wasAdded = trustlineAdded === assetKey;
            return (
              <div style={{
                marginTop: 6, padding: '6px 8px', borderRadius: 5,
                background: '#fff', border: `1px dashed ${C.red}44`,
                color: C.textMuted, fontSize: 11, fontFamily: 'monospace',
                display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap',
                wordBreak: 'break-all',
              }}>
                <div style={{ flex: '1 1 240px', minWidth: 240 }}>
                  Asset: <strong>{err.asset!.code}</strong> · Issuer: {err.asset!.issuer}
                </div>
                {canAdd && !wasAdded && (
                  <button
                    type="button"
                    onClick={() => void handleAddTrustline(err.asset!)}
                    disabled={isAdding}
                    style={{
                      padding: '4px 10px', borderRadius: 4, border: 0,
                      background: C.red, color: '#fff', fontSize: 11,
                      fontWeight: 600, fontFamily: 'inherit',
                      cursor: isAdding ? 'wait' : 'pointer',
                      whiteSpace: 'nowrap',
                      opacity: isAdding ? 0.7 : 1,
                    }}
                    title={`Open a trustline to ${err.asset!.code}:${err.asset!.issuer.slice(0, 8)}… so trades can settle on the new Zenex contract`}
                  >
                    {isAdding ? 'Adding…' : `+ Add ${err.asset!.code} trustline`}
                  </button>
                )}
                {wasAdded && (
                  <span style={{
                    padding: '4px 10px', borderRadius: 4,
                    background: C.green, color: '#fff', fontSize: 11,
                    fontWeight: 600, whiteSpace: 'nowrap',
                  }}>
                    ✓ trustline added — retrying…
                  </span>
                )}
              </div>
            );
          })()}
          {/* Position-shaped failures (PositionNotFound / PositionTooNew /
              PositionNotPending) are easiest to diagnose on the canonical
              Zenex dashboard, so surface a deep-link as a call-to-action. */}
          {err?.code && /position/i.test(err.code) && (
            <div style={{ marginTop: 6 }}>
              <a
                href={ZENEX_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: C.red, fontSize: 11, textDecoration: 'underline',
                }}
              >
                Check your Zenex positions ↗
              </a>
            </div>
          )}
          {err?.raw && err.raw !== err.message && (
            <details style={{ marginTop: 6 }}>
              <summary style={{ cursor: 'pointer', fontSize: 11, color: C.textMuted }}>
                Show raw simulation error
              </summary>
              <pre style={{
                marginTop: 4, padding: 6, borderRadius: 4,
                background: '#fff', border: `1px solid ${C.borderFaint}`,
                color: C.textMuted, fontSize: 10, whiteSpace: 'pre-wrap',
                wordBreak: 'break-word', maxHeight: 180, overflow: 'auto',
              }}>{err.raw}</pre>
            </details>
          )}
        </div>
      )}

      {/* ── Last settled ───────────────────────────────────────────────────── */}
      {lastHash && (
        <div style={{
          margin: '10px 14px', padding: '9px 12px', borderRadius: 7,
          background: C.greenDim, border: `1px solid ${C.green}44`,
          color: C.green, fontSize: 12,
        }}>
          ✓ Settled {lastHash.id.slice(0, 24)}… ·{' '}
          <a
            href={`https://stellar.expert/explorer/${network}/tx/${lastHash.hash}`}
            target="_blank" rel="noopener noreferrer"
            style={{ color: C.green, textDecoration: 'underline' }}
          >
            view on Stellar Expert ↗
          </a>
          {' · '}
          <a
            href={ZENEX_APP_URL}
            target="_blank" rel="noopener noreferrer"
            style={{ color: C.green, textDecoration: 'underline' }}
          >
            view on Zenex ↗
          </a>
        </div>
      )}

      {/* ── Hidden close-trades hint ───────────────────────────────────────── */}
      {(openPositions.status === 'loading' || hiddenCloseCount > 0) && (
        <div style={{
          margin: '10px 14px', padding: '9px 12px', borderRadius: 7,
          background: '#f5f5f5', border: `1px solid ${C.borderFaint}`,
          color: C.textMuted, fontSize: 12,
        }}>
          {openPositions.status === 'loading'
            ? '⟳ Checking Zenex for your open positions…'
            : (
              <>
                ℹ {hiddenCloseCount} close-position rule{hiddenCloseCount === 1 ? '' : 's'} hidden — you have no matching open Zenex position right now.
                {' '}
                <a
                  href={ZENEX_APP_URL}
                  target="_blank" rel="noopener noreferrer"
                  style={{ color: C.goldDark, textDecoration: 'underline' }}
                >
                  Open Zenex ↗
                </a>
              </>
            )}
        </div>
      )}

      {/* ── List ────────────────────────────────────────────────────────────── */}
      <div style={{ padding: '10px 14px 16px' }}>
        {visibleActionable === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '28px 0', color: C.textFaint, fontSize: 12 }}>
            No pending Zenex trades.
          </div>
        )}

        {visibleOpens.map(trade => {
          const seedMid = openForms[trade.id]?.marketId
                       ?? Number(trade.parameters?.market_id ?? trade.price_hint?.market_id ?? 0);
          const market  = markets[String(seedMid)];
          return (
            <TradeCard
              key={trade.id}
              trade={trade}
              kind="open"
              network={network}
              expanded={expanded === trade.id}
              onToggle={() => setExpanded(prev => (prev === trade.id ? null : trade.id))}
              executing={executing === trade.id}
              cancelling={cancelling === trade.id}
              canAct={!executing && !cancelling}
              onExecute={() => executeOpen(trade)}
              onCancel={handleCancel}
              markets={markets}
              openForm={openForms[trade.id]}
              onOpenFormChange={updateOpenForm}
              markPriceUsd={market ? markPrices[market.feed_id]?.priceUsd : undefined}
              priceDecimals={market?.price_decimals}
            />
          );
        })}

        {visibleCloseCards.map(({ trade, position, cardId }) => {
          const market = markets[String(position.market_id)];
          return (
            <TradeCard
              key={cardId}
              trade={trade}
              kind="close"
              network={network}
              expanded={expanded === cardId}
              onToggle={() => setExpanded(prev => (prev === cardId ? null : cardId))}
              executing={executing === cardId}
              cancelling={cancelling === trade.id}
              canAct={!executing && !cancelling}
              onExecute={() => executeClose(trade, position)}
              onCancel={handleCancel}
              markets={markets}
              openPositionInfo={position}
              markPriceUsd={market ? markPrices[market.feed_id]?.priceUsd : undefined}
              priceDecimals={market?.price_decimals}
            />
          );
        })}
      </div>
    </div>
  );
};

// ── Card ─────────────────────────────────────────────────────────────────────

interface TradeCardProps {
  trade:     PendingTradeAction;
  kind:      'open' | 'close';
  network:   'testnet' | 'mainnet';
  expanded:  boolean;
  executing: boolean;
  cancelling:boolean;
  canAct:    boolean;
  markets:   Record<string, ZenexMarketInfo>;
  onToggle:  () => void;
  /** Parent binds the trade (and close-position, when applicable) so the
   *  card doesn't need to know about the fan-out. */
  onExecute: () => void | Promise<void>;
  onCancel:  (trade: PendingTradeAction, reason: CancelReason) => void | Promise<void>;
  // Open-only:
  openForm?:          OpenForm;
  onOpenFormChange?:  (tradeId: string, patch: Partial<OpenForm>) => void;
  // Close-only — the specific position this card is bound to.
  openPositionInfo?:  ZenexOpenPosition | null;
  /** Live mark price (USD) for this card's market, polled at panel level.
   *  Undefined until the first Pyth fetch resolves. */
  markPriceUsd?:      number;
  /** Decimals for the market's price feed (defaults to DEFAULT_PRICE_DECIMS). */
  priceDecimals?:     number;
}

const TradeCard: React.FC<TradeCardProps> = ({
  trade, kind, network, expanded, executing, cancelling, canAct, markets,
  onToggle, onExecute, onCancel,
  openForm, onOpenFormChange,
  openPositionInfo,
  markPriceUsd,
  priceDecimals,
}) => {
  const cfg = STATUS_CONFIG[trade.status] ?? STATUS_CONFIG.pending;
  const [cancelReason, setCancelReason] = useState<CancelReason>('user_declined');

  // For close cards, derive the live PnL once per render. Returns null
  // when we can't compute it yet (e.g. first paint before Pyth resolved,
  // or the singular position-lookup variant didn't populate entry/collateral).
  const pnl = useMemo(
    () => (kind === 'close' && openPositionInfo
            ? computePnL(openPositionInfo, markPriceUsd, priceDecimals)
            : null),
    [kind, openPositionInfo, markPriceUsd, priceDecimals],
  );

  const shortAddr = (addr: string) => (addr ? `${addr.slice(0, 8)}…${addr.slice(-6)}` : '—');

  // Resolve the display symbol. For open: use the form-selected market id.
  // For close: use the on-chain open position's market id.
  const resolvedMarketId = kind === 'open'
    ? openForm?.marketId ?? Number(trade.parameters?.market_id ?? 0)
    : (openPositionInfo?.market_id ?? Number(trade.parameters?.market_id ?? 0));
  const symbol =
    (resolvedMarketId > 0 && markets[String(resolvedMarketId)]?.symbol)
    ?? (resolvedMarketId > 0 ? `market ${resolvedMarketId}` : 'market not set');

  const side = openForm?.isLong
    ? 'LONG'
    : openForm?.isLong === false
      ? 'SHORT'
      : (trade.parameters?.is_long === true || trade.parameters?.is_long === 'true' ? 'LONG' : 'SHORT');

  return (
    <div
      className="gzt-card"
      style={{
        background: C.bgCard,
        // Neutral 1px border — the status is already communicated loudly
        // by the colored pill in the header, and the 2px alpha-tinted
        // border for `pending` (amber @ 27% alpha) was blending to a
        // warm beige/peach on the white surface. Keep the surface cool.
        border: `1px solid ${C.border}`,
        borderRadius: 9,
        marginBottom: 10,
        overflow: 'hidden',
        boxShadow: '0 1px 4px rgba(0,0,0,.06)',
      }}
    >
      {/* ── Summary ─────────────────────────────────────────────────────────── */}
      <div onClick={onToggle} style={{ padding: '11px 13px', cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span className={
            `gzt-chip ${
              trade.status === 'pending'   ? 'status-pending'
            : trade.status === 'completed' ? 'status-completed'
            : trade.status === 'cancelled' ? 'status-cancelled'
            :                                'status-neutral'
            }`
          }>
            {cfg.icon} {cfg.label}
          </span>
          <span className={`gzt-chip kind-${kind === 'open' ? 'open' : 'close'}`}>
            {kind === 'open' ? 'OPEN MARKET' : 'CLOSE POSITION'}
          </span>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#000', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {trade.rule_name || trade.function_name || 'Zenex Trade'}
          </span>
          <span style={{ fontSize: 11, color: C.textFaint }}>{expanded ? '▲' : '▼'}</span>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#000' }}>
            {symbol}{' '}
            {kind === 'open' && (
              <span style={{
                fontSize: 10, fontWeight: 700, letterSpacing: '.3px',
                padding: '2px 8px', borderRadius: 4,
                background: side === 'LONG' ? ZX.long      : ZX.short,
                color:      '#fff',
                border:     `1px solid ${side === 'LONG' ? ZX.longDeep : ZX.shortDeep}`,
                marginLeft: 4,
              }}>
                {side}
              </span>
            )}
          </span>
          <span className="gzt-chip network">
            {trade.network}
          </span>
          {kind === 'open' && openForm && (
            <>
              <span style={{ fontSize: 11, color: C.textMuted }}>
                Collateral <strong style={{ color: '#000' }}>{openForm.collateralUsdc || '0'} USDC</strong>
              </span>
              <span style={{ fontSize: 11, color: C.textMuted }}>
                Leverage <strong style={{ color: ZX.longDeep }}>{openForm.leverage}x</strong>
              </span>
              <span style={{ fontSize: 11, color: C.textMuted }}>
                Notional <strong style={{ color: '#000' }}>{deriveNotionalUsdc(openForm)} USDC</strong>
              </span>
              <span style={{ fontSize: 11, color: C.textMuted }}>
                Mark <strong style={{ color: '#000' }}>{formatUsdPrice(markPriceUsd)}</strong>
              </span>
            </>
          )}
          {kind === 'close' && openPositionInfo && (
            <>
              <span style={{ fontSize: 11, color: C.textMuted }}>
                Position <strong style={{ color: '#000' }}>#{openPositionInfo.id}</strong>
              </span>
              {pnl ? (
                <>
                  <span style={{ fontSize: 11, color: C.textMuted }}>
                    Entry <strong style={{ color: '#000' }}>{formatUsdPrice(pnl.entryUsd)}</strong>
                  </span>
                  <span style={{ fontSize: 11, color: C.textMuted }}>
                    Mark <strong style={{ color: '#000' }}>{formatUsdPrice(pnl.markUsd)}</strong>
                  </span>
                  <span style={{
                    fontSize: 11, fontWeight: 700,
                    color: pnl.pnlUsdc >= 0 ? ZX.longDeep : ZX.shortDeep,
                  }}
                  title="Mark PnL — price-only, before Zenex funding/borrow fees"
                  >
                    Mark PnL {formatUsdcDelta(pnl.pnlUsdc)} ({formatPctDelta(pnl.returnPctOnCollateral)})
                  </span>
                </>
              ) : (
                <span style={{ fontSize: 11, color: C.textMuted }}>
                  Notional <strong style={{ color: '#000' }}>
                    {(Number(openPositionInfo.notional_size) / 10 ** USDC_DECIMALS).toFixed(4)} USDC
                  </strong>
                  {markPriceUsd != null && (
                    <>{' '}· Mark <strong style={{ color: '#000' }}>{formatUsdPrice(markPriceUsd)}</strong></>
                  )}
                </span>
              )}
            </>
          )}
          <span style={{ fontSize: 10, color: C.textFaint, marginLeft: 'auto' }}>
            {new Date(trade.received_at).toLocaleString()}
          </span>
        </div>
      </div>

      {/* ── Detail ──────────────────────────────────────────────────────────── */}
      {expanded && (
        <div style={{ borderTop: `1px solid ${C.borderFaint}`, padding: '12px 14px', background: '#fff' }}>

          {/* Open form — editable params */}
          {kind === 'open' && openForm && onOpenFormChange && (
            <div style={{
              padding: 12, borderRadius: 7, marginBottom: 12,
              background: '#f5f7f8', border: `1px solid ${C.border}`,
            }}>
              <div style={{
                fontSize: 11, fontWeight: 700, color: C.text, marginBottom: 10,
                textTransform: 'uppercase', letterSpacing: '.4px',
              }}>
                Trade Parameters
              </div>

              {/* Market + Side */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
                <div>
                  <div className="gzt-label">Market</div>
                  <select
                    className="gzt-input"
                    value={openForm.marketId}
                    onChange={(e) => onOpenFormChange(trade.id, { marketId: Number(e.target.value) })}
                    onClick={(e) => e.stopPropagation()}
                    disabled={executing || cancelling}
                  >
                    {Object.keys(markets).length === 0 && (
                      <option value={openForm.marketId}>market {openForm.marketId}</option>
                    )}
                    {Object.entries(markets).map(([id, m]) => (
                      <option key={id} value={Number(id)}>
                        #{id} · {m.symbol} (feed {m.feed_id})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="gzt-label">Side</div>
                  <div className="gzt-seg" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      className={`long ${openForm.isLong ? 'active' : ''}`}
                      onClick={() => onOpenFormChange(trade.id, { isLong: true })}
                      disabled={executing || cancelling}
                    >
                      ⬆ LONG
                    </button>
                    <button
                      type="button"
                      className={`short ${!openForm.isLong ? 'active' : ''}`}
                      onClick={() => onOpenFormChange(trade.id, { isLong: false })}
                      disabled={executing || cancelling}
                    >
                      ⬇ SHORT
                    </button>
                  </div>
                </div>
              </div>

              {/* Collateral (full-width — leverage + derived notional live below) */}
              <div style={{ marginBottom: 10 }}>
                <div className="gzt-label">Collateral (USDC)</div>
                <input
                  className="gzt-input"
                  type="number"
                  min="0"
                  step="0.0000001"
                  value={openForm.collateralUsdc}
                  onChange={(e) => onOpenFormChange(trade.id, { collateralUsdc: e.target.value })}
                  onClick={(e) => e.stopPropagation()}
                  placeholder="10"
                  disabled={executing || cancelling}
                />
              </div>

              {/* Leverage — stepper + slider + presets, mirroring zenex.trade */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                  <div className="gzt-label" style={{ marginBottom: 0 }}>Leverage</div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: ZX.longDeep }}>
                    {openForm.leverage}x
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenFormChange(trade.id, { leverage: clampLeverage(openForm.leverage - 1) });
                    }}
                    disabled={executing || cancelling || openForm.leverage <= MIN_LEVERAGE}
                    style={{
                      width: 28, height: 28, borderRadius: 6, border: `1px solid ${C.border}`,
                      background: '#fff', color: C.text, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                    }}
                    title="Decrease leverage"
                  >−</button>
                  <input
                    type="range"
                    min={MIN_LEVERAGE}
                    max={MAX_LEVERAGE}
                    step={1}
                    value={openForm.leverage}
                    onChange={(e) => onOpenFormChange(trade.id, { leverage: clampLeverage(Number(e.target.value)) })}
                    onClick={(e) => e.stopPropagation()}
                    disabled={executing || cancelling}
                    style={{ flex: 1, accentColor: ZX.long }}
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onOpenFormChange(trade.id, { leverage: clampLeverage(openForm.leverage + 1) });
                    }}
                    disabled={executing || cancelling || openForm.leverage >= MAX_LEVERAGE}
                    style={{
                      width: 28, height: 28, borderRadius: 6, border: `1px solid ${C.border}`,
                      background: '#fff', color: C.text, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                    }}
                    title="Increase leverage"
                  >+</button>
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                  {LEVERAGE_PRESETS.map(preset => {
                    const active = openForm.leverage === preset;
                    return (
                      <button
                        key={preset}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenFormChange(trade.id, { leverage: preset });
                        }}
                        disabled={executing || cancelling}
                        style={{
                          flex: '1 1 0',
                          padding: '6px 0',
                          fontSize: 11, fontWeight: 700,
                          borderRadius: 6,
                          border: `1px solid ${active ? ZX.long : C.border}`,
                          background: active ? ZX.longDim : '#fff',
                          color: active ? ZX.longDeep : C.textMuted,
                          cursor: 'pointer',
                        }}
                      >
                        {preset}x
                      </button>
                    );
                  })}
                </div>
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 8, fontFamily: 'monospace' }}>
                  Notional = <strong style={{ color: '#000' }}>{deriveNotionalUsdc(openForm)} USDC</strong>
                  {' '}<span style={{ color: C.textFaint }}>
                    ({openForm.collateralUsdc || '0'} × {openForm.leverage}x)
                  </span>
                </div>
              </div>

              {/* TP / SL */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 4 }}>
                <div>
                  <div className="gzt-label">Take Profit (USD, optional)</div>
                  <input
                    className="gzt-input"
                    type="number"
                    min="0"
                    step="0.00000001"
                    value={openForm.takeProfitPrice}
                    onChange={(e) => onOpenFormChange(trade.id, { takeProfitPrice: e.target.value })}
                    onClick={(e) => e.stopPropagation()}
                    placeholder="0 = none"
                    disabled={executing || cancelling}
                  />
                </div>
                <div>
                  <div className="gzt-label">Stop Loss (USD, optional)</div>
                  <input
                    className="gzt-input"
                    type="number"
                    min="0"
                    step="0.00000001"
                    value={openForm.stopLossPrice}
                    onChange={(e) => onOpenFormChange(trade.id, { stopLossPrice: e.target.value })}
                    onClick={(e) => e.stopPropagation()}
                    placeholder="0 = none"
                    disabled={executing || cancelling}
                  />
                </div>
              </div>

              <div style={{ fontSize: 10, color: C.textMuted, marginTop: 8, lineHeight: 1.5 }}>
                Notional = collateral × leverage, submitted to the contract at 7-decimal USDC precision.
                Price is always fetched server-side from Pyth and passed to the contract as opaque bytes —
                you don&apos;t need to set it. TP/SL convert at 8 decimals (USD price); a value of 0 disables the trigger.
              </div>
            </div>
          )}

          {/* Close — resolved on-chain position + live PnL panel */}
          {kind === 'close' && openPositionInfo && (
            <div style={{
              padding: 12, borderRadius: 7, marginBottom: 12,
              background: '#f5f7f8', border: `1px solid ${C.border}`,
            }}>
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: 8,
              }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: C.text,
                  textTransform: 'uppercase', letterSpacing: '.4px',
                }}>
                  Position #{openPositionInfo.id} ·{' '}
                  {markets[String(openPositionInfo.market_id)]?.symbol ?? `market ${openPositionInfo.market_id}`}
                  {openPositionInfo.is_long != null && (
                    <span style={{
                      marginLeft: 6, padding: '1px 7px', borderRadius: 3,
                      fontSize: 9, fontWeight: 800,
                      color: '#fff',
                      background: openPositionInfo.is_long ? ZX.long : ZX.short,
                    }}>
                      {openPositionInfo.is_long ? 'LONG' : 'SHORT'}
                    </span>
                  )}
                </div>
                {pnl && (
                  <div
                    style={{
                      fontSize: 14, fontWeight: 800, fontFamily: 'monospace',
                      color: pnl.pnlUsdc >= 0 ? ZX.longDeep : ZX.shortDeep,
                      textAlign: 'right',
                    }}
                    title="Mark PnL — based on price movement only, before Zenex funding/borrow fees"
                  >
                    <div style={{
                      fontSize: 9, fontWeight: 700, letterSpacing: '.4px',
                      color: C.textFaint, fontFamily: 'inherit',
                    }}>
                      MARK PNL
                    </div>
                    {formatUsdcDelta(pnl.pnlUsdc)}
                    <span style={{ fontSize: 11, marginLeft: 6, fontWeight: 700 }}>
                      ({formatPctDelta(pnl.returnPctOnCollateral)})
                    </span>
                  </div>
                )}
              </div>

              {pnl ? (
                <div style={{
                  display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8,
                  fontSize: 11, fontFamily: 'monospace', lineHeight: 1.4,
                }}>
                  <div>
                    <div style={{ color: C.textFaint, fontSize: 9, textTransform: 'uppercase', letterSpacing: '.4px' }}>Entry</div>
                    <div style={{ color: '#000', fontWeight: 700 }}>{formatUsdPrice(pnl.entryUsd)}</div>
                  </div>
                  <div>
                    <div style={{ color: C.textFaint, fontSize: 9, textTransform: 'uppercase', letterSpacing: '.4px' }}>Mark</div>
                    <div style={{ color: '#000', fontWeight: 700 }}>{formatUsdPrice(pnl.markUsd)}</div>
                  </div>
                  <div>
                    <div style={{ color: C.textFaint, fontSize: 9, textTransform: 'uppercase', letterSpacing: '.4px' }}>Δ Price</div>
                    <div style={{
                      color: pnl.priceChangePct >= 0 ? ZX.longDeep : ZX.shortDeep,
                      fontWeight: 700,
                    }}>
                      {formatPctDelta(pnl.priceChangePct)}
                    </div>
                  </div>
                  <div>
                    <div style={{ color: C.textFaint, fontSize: 9, textTransform: 'uppercase', letterSpacing: '.4px' }}>Collateral</div>
                    <div style={{ color: '#000', fontWeight: 700 }}>{pnl.collateralUsdc.toFixed(4)} USDC</div>
                  </div>
                  <div>
                    <div style={{ color: C.textFaint, fontSize: 9, textTransform: 'uppercase', letterSpacing: '.4px' }}>Notional</div>
                    <div style={{ color: '#000', fontWeight: 700 }}>{pnl.notionalUsdc.toFixed(4)} USDC</div>
                  </div>
                  <div>
                    <div style={{ color: C.textFaint, fontSize: 9, textTransform: 'uppercase', letterSpacing: '.4px' }}>Net Value</div>
                    <div style={{
                      color: pnl.netValueUsdc >= pnl.collateralUsdc ? ZX.longDeep : ZX.shortDeep,
                      fontWeight: 700,
                    }}>
                      {pnl.netValueUsdc.toFixed(4)} USDC
                    </div>
                  </div>
                </div>
              ) : null}

              {pnl && (
                <div style={{
                  marginTop: 8, paddingTop: 8, borderTop: `1px dashed ${C.border}`,
                  fontSize: 10, color: C.textFaint, lineHeight: 1.5,
                }}>
                  Mark PnL is computed from price movement only
                  ({pnl.notionalUsdc.toFixed(2)} USDC notional × {formatPctDelta(pnl.priceChangePct)}).
                  Zenex deducts borrow / funding fees from your collateral over time, so the
                  fee-net total on{' '}
                  <a
                    href={ZENEX_APP_URL}
                    target="_blank" rel="noopener noreferrer"
                    style={{ color: ZX.longDeep, textDecoration: 'underline' }}
                  >
                    zenex.trade
                  </a>{' '}
                  may differ slightly.
                </div>
              )}
              {!pnl && (
                <div style={{ fontSize: 11, color: C.textMuted, fontFamily: 'monospace', lineHeight: 1.6 }}>
                  Notional: <strong>
                    {(Number(openPositionInfo.notional_size) / 10 ** USDC_DECIMALS).toFixed(4)} USDC
                  </strong>
                  {markPriceUsd != null && (
                    <> · Mark: <strong>{formatUsdPrice(markPriceUsd)}</strong></>
                  )}
                  <div style={{ marginTop: 4, color: C.textFaint, fontSize: 10 }}>
                    Live PnL unavailable — entry price / collateral not populated by the on-chain lookup yet.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Metadata grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', marginBottom: 10 }}>
            {([
              ['Rule', trade.rule_name ?? trade.rule_id],
              ['Rule ID', trade.rule_id],
              ['Contract', shortAddr(trade.contract_address)],
              ['Function', trade.function_name],
              ['Matched user', shortAddr(trade.matched_public_key)],
              trade.location ? ['Location', `${trade.location.latitude?.toFixed(4)}, ${trade.location.longitude?.toFixed(4)}`] : null,
              trade.expires_at ? ['Expires', new Date(trade.expires_at).toLocaleString()] : null,
            ] as Array<[string, React.ReactNode] | null>).filter(Boolean).map((row) => {
              const [label, value] = row as [string, React.ReactNode];
              return (
                <div key={label}>
                  <div style={{ fontSize: 9, color: C.textFaint, textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 2 }}>
                    {label}
                  </div>
                  <div style={{ fontSize: 11, color: '#333', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {value as string | number}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Raw parameters dump */}
          {trade.parameters && Object.keys(trade.parameters).length > 0 && (
            <details style={{ marginBottom: 10 }}>
              <summary style={{ fontSize: 11, color: C.textMuted, cursor: 'pointer' }}>
                Raw parameters ({Object.keys(trade.parameters).length})
              </summary>
              <pre style={{
                marginTop: 6, padding: '8px 10px', background: '#f5f5f5', borderRadius: 6,
                border: `1px solid ${C.borderFaint}`, fontSize: 10, lineHeight: 1.5,
                color: C.textMuted, maxHeight: 180, overflow: 'auto',
              }}>
                {JSON.stringify(trade.parameters, null, 2)}
              </pre>
            </details>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              className={
                // open LONG  → Zenex emerald fill + green glow
                // open SHORT → Zenex red-pink fill + red glow
                // close      → plain GeoTrust gold (the "act soon" pulse
                //   lives on the CLOSE POSITION kind-chip at the top of
                //   the card instead, keeping the action button calm).
                kind === 'open' && openForm
                  ? `gzt-exec ${openForm.isLong ? 'zx-long' : 'zx-short'}`
                  : 'gzt-exec'
              }
              style={{ padding: '8px 20px', fontSize: 13, fontWeight: 700 }}
              onClick={(e) => { e.stopPropagation(); onExecute(); }}
              disabled={!canAct || executing || cancelling}
            >
              {executing
                ? '⏳ Executing…'
                : kind === 'open'
                  ? (openForm?.isLong ? '⬆ LONG / BUY' : '⬇ SHORT / SELL')
                  : '⬇ CLOSE POSITION'}
            </button>

            <select
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value as CancelReason)}
              onClick={(e) => e.stopPropagation()}
              style={{
                fontSize: 11, padding: '6px 10px', borderRadius: 6,
                border: `1px solid ${C.border}`, background: '#fff', color: C.text,
              }}
              disabled={executing || cancelling}
            >
              {CANCEL_REASONS.map(r => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>

            <button
              className="gzt-cancel"
              style={{ padding: '7px 14px', fontSize: 12, fontWeight: 600 }}
              onClick={(e) => { e.stopPropagation(); onCancel(trade, cancelReason); }}
              disabled={!canAct || executing || cancelling}
            >
              {cancelling ? 'Cancelling…' : '✕ Cancel'}
            </button>
          </div>

          <div style={{
            fontSize: 10, color: C.textFaint, marginTop: 10, padding: '6px 9px',
            borderRadius: 5, background: '#f5f5f5', border: `1px solid ${C.borderFaint}`,
          }}>
            {kind === 'open'
              ? 'GeoTrust fetches the Pyth price bytes, signs with your passkey, and submits `open_market` with the parameters above.'
              : 'GeoTrust fetches the Pyth price bytes for this market, signs with your passkey, and submits `close_position` against the position shown.'}
            {' '}Network: <strong style={{ color: C.text }}>{network}</strong>
          </div>
        </div>
      )}
    </div>
  );
};

export default GeoLinkPendingTradesPanel;
